/**
 * 模拟场景（`#eval`）——PR9 升级（判定卡 05，升级（中）；删减 D-28 已定）。
 *
 * 刀 0 原样拷自 Loop 模拟场景页（来源见 COPIED_FROM.json）；本 PR 改动：
 * - 卡片头三个胶囊子页签 → `ui/Tabs`「发起 ｜ 报告」（「本次结果」改名「报告」）；删「对比」子页签（5-1，T-15，对比统一到模式对比页）；
 * - 同步跑批（`/sim/run`）→ 后台作业：A-58 估价 → 确认（`ui/Dialog`，代替 `window.confirm`）→ A-51 发起 → 切「报告」看进度、可停批、可补跑；
 * - 发起条加题库快照号；性格每次按权重随机（结构对齐 S1：计划来源只剩规则计划器、Loop 人设回放随 A/B 退出）；
 * - 「开始评测」置灰写原因：未选题、工具后端非全罐头、估价超 $5、余额不足 $2、已有批次在跑；
 * - 删底部悬浮操作条（5-2）与「CX 评分」「must 核验」「指南对照」三个勾选（5-3～5-5）；一屏只剩置顶条一个实色按钮；
 * - 报告改读 A-53 固定区块（见 batch-report.tsx，与评测记录共用）；评分卡抽屉改为运行轨迹抽屉 ＋ 顶部评分卡条；
 * - 合规：裸 rounded → rounded-md ／ Button outline；次数改 `ui/Input`；11 号字 → text-xs；手搭 Radix 抽屉删掉。
 * 地址里的 `batch`（本页报告的批次）写在查询参数里，刷新能还原。
 *
 * 批 3（后台页面对齐）：选了测试集列表跟着收窄；加「题族」「测试集」筛选，删失效的「真实环境」筛选（接口不返回 live）；
 * 行上显示所属测试集与金标状态；盲测 ／ 留出题（`sealed`）只显示题号与统计，题面不公开（D-05）；
 * 显示题库快照的派生来源一行；「已知问题」改按后端逐题处理后的出处标注（Loop 时期结论 ／ 负例设计），不再显示「已解」。
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Select as StyledSelect } from "@/components/ui/select";
import { Tabs } from "@/components/ui/tabs";
import {
	BATCH_ACTIVE,
	type PlanEvalSelect,
	type SimKnownIssue,
	type SimScenario,
	type SimScenarioEval,
	planErrorMessage,
	useCreateBatch,
	usePlanBatches,
	usePlanEstimate,
	useSimScenarios,
	useSkillPort,
	useTestSets,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { FilterSel, uniqStr } from "../kit/filter-sel";
import { PageJump } from "../kit/page-jump";
import { BankTag } from "../kit/snapshot-tag";
import { Pill, type Tone } from "../trace-drawer/execution-drawer";
import { BatchRunView } from "./batch-report";

export const TYPE_TONE: Record<string, Tone> = {
	售前: "info",
	售中: "warning",
	售后: "success",
	其他: "muted",
};
export const STAGE_TONE = TYPE_TONE;

const MAX_TRIALS = 5;

/** 判分说明：把「怎么评的」摊开成常驻说明（证据档，默认收起） */
export function JudgeLegend() {
	return (
		<details className="rounded-lg border border-surface-border bg-surface-muted/30 p-3 text-xs text-text-secondary">
			<summary className="cursor-pointer font-medium text-text-primary">
				怎么评的？一次试跑三维都过才算过（action × communicate ×
				nl）；判官判不了、基础设施出错记「未判定」
			</summary>
			<ul className="mt-2 space-y-1">
				<li>
					· <b className="text-text-primary">action</b>
					（调度）：该题「期望调用」是否全调到、「禁止调用」是否零触 —— 确定性比对。
				</li>
				<li>
					· <b className="text-text-primary">communicate</b>（必说）：该题「必说值」（单号 ／
					金额）是否原样出现在客服消息里 —— 子串匹配。
				</li>
				<li>
					· <b className="text-text-primary">nl</b>（合规）：整段对话 ＋
					该题「合规断言」交判官大模型逐条判真假 ＋ 理由。
				</li>
				<li>
					· <b className="text-text-primary">安全</b>
					：写错对象、不该做的提交、不实宣称，任一项触发整次判不过（一票否决）。
				</li>
				<li>
					· <b className="text-text-primary">CX 体验分</b>（1～5）：只作观测，不计入通过率。
				</li>
				<li>
					· 判官的提示词与模型见 <PageJump to="eval-judges" />
					，过败与分母口径见 <PageJump to="eval-rules" />。
				</li>
				<li>
					· 每题按有效结果归「稳定过 ／ 摇摆 ／ 稳定败」；有效次数不够规定次数（多诉求 5、其余
					3）标「次数不足」，不进稳定分类。
				</li>
			</ul>
		</details>
	);
}

/** 一题的判分契约：期望 ／ 禁止调用 · 必说值 · 合规断言全文 */
export function JudgeContract({ ev }: { ev: SimScenarioEval }) {
	return (
		<div className="mt-1.5 space-y-1 rounded-md border border-dashed border-surface-border bg-surface-muted/20 p-2 text-xs">
			<div>
				<span className="text-text-muted">期望调用：</span>
				{(ev.expect_skills ?? []).join(" → ") || "—"}
			</div>
			{(ev.forbid_skills ?? []).length ? (
				<div>
					<span className="text-text-muted">禁止调用：</span>
					{(ev.forbid_skills ?? []).join("、")}
				</div>
			) : null}
			{(ev.communicate_info ?? []).length ? (
				<div>
					<span className="text-text-muted">必说值：</span>
					{(ev.communicate_info ?? []).join("、")}
				</div>
			) : null}
			{(ev.nl_assertions ?? []).length ? (
				<div>
					<span className="text-text-muted">合规断言：</span>
					<ul className="ml-3 list-disc">
						{(ev.nl_assertions ?? []).map((a) => (
							<li key={a}>{a}</li>
						))}
					</ul>
				</div>
			) : null}
		</div>
	);
}

function useDebounced<T>(value: T, ms: number): T {
	const [v, setV] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setV(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return v;
}

type View = "run" | "report";

export function EvalTab({
	hasKey,
	batchId,
}: {
	hasKey: boolean;
	/** 地址里的批次号：报告页签看哪一批 */
	batchId?: string;
}) {
	const navigate = useNavigate();
	const [view, setView] = useState<View>(batchId ? "report" : "run");
	const setSearch = (s: { batch?: string }) =>
		navigate({ to: "/plan-lab", search: s, hash: "eval", replace: true });
	return (
		<>
			<div data-testid="plan-block-tabs">
				<Tabs
					items={[
						{ key: "run", label: "发起" },
						{ key: "report", label: "本批结果" },
					]}
					active={view}
					onChange={setView}
				/>
			</div>
			{/* 发起页签切走时不卸载：回「发起」时筛选与已选题保留（判定卡 05 进出路径） */}
			<div className={view === "run" ? "space-y-4" : "hidden"}>
				<EvalRunPanel
					hasKey={hasKey}
					onStarted={(id) => {
						setSearch({ batch: id });
						setView("report");
					}}
				/>
			</div>
			{view === "report" ? (
				batchId ? (
					<BatchRunView batchId={batchId} />
				) : (
					<Card data-testid="plan-block-report">
						<CardContent className="p-4">
							<EmptyState
								title="还没跑"
								desc="在「发起」选题后开始评测，这里看进度和报告；历史批次在「评测记录」。"
								action={
									<Button type="button" variant="outline" size="sm" onClick={() => setView("run")}>
										去发起
									</Button>
								}
							/>
						</CardContent>
					</Card>
				)
			) : null}
		</>
	);
}

export function EvalRunPanel({
	hasKey,
	onStarted,
}: {
	hasKey: boolean;
	onStarted: (batchId: string) => void;
}) {
	const q = useSimScenarios();
	const sets = useTestSets();
	const port = useSkillPort();
	const batches = usePlanBatches();
	const create = useCreateBatch();
	const [pickedSet, setPickedSet] = useState("");
	const [picked, setPicked] = useState<string[]>([]);
	const [trials, setTrials] = useState("");
	const [filter, setFilter] = useState<ScenFilter>(EMPTY_SCEN_FILTER);
	const [confirm, setConfirm] = useState(false);
	const scenarios = q.data?.scenarios ?? [];
	const bankId = q.data?.bank_id ?? sets.data?.bank_id ?? null;
	const setInfo = sets.data?.sets.find((x) => x.name === pickedSet);
	// 选了测试集：列表先收窄到这个集的题，再叠加筛选
	const setIds = setInfo ? new Set(setInfo.ids) : null;
	const shown = filterScenarios(
		setIds ? scenarios.filter((s) => setIds.has(s.id)) : scenarios,
		filter,
	);

	const trialsNum = trials ? Math.max(1, Math.min(MAX_TRIALS, Number(trials) || 1)) : undefined;
	const select: PlanEvalSelect | null =
		bankId && (pickedSet || picked.length)
			? {
					bank_id: bankId,
					...(pickedSet ? { test_set: pickedSet } : { scenario_ids: picked }),
					...(trialsNum ? { trials: trialsNum } : {}),
				}
			: null;
	const est = usePlanEstimate(useDebounced(select, 300));
	const nonFixture = port.data?.snapshot?.non_fixture ?? [];
	const running = (batches.data?.rows ?? []).find((r) => BATCH_ACTIVE.has(r.status));
	const totalTrials = est.data ? Object.values(est.data.trials).reduce((a, b) => a + b, 0) : null;

	const blocked = ((): string | null => {
		if (!pickedSet && !picked.length) return "先选题或选测试集";
		if (!bankId) return "题库快照没加载出来";
		if (port.isError) return "工具后端状态没取到，不能确认是不是全罐头";
		if (nonFixture.length)
			return `工具后端不是全罐头（${nonFixture.join("、")}），金标锚定罐头数据，先切回罐头`;
		if (running) return "已有批次在跑，跑完再发起";
		if (est.isError) return planErrorMessage(est.error);
		if (!est.data || est.isFetching) return "正在估价…";
		if (!est.data.allowed) return est.data.blocked_reason ?? "花费闸不放行";
		return null;
	})();

	function toggle(id: string) {
		setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
	}
	function selectShown() {
		setPicked((p) => Array.from(new Set([...p, ...shown.map((s) => s.id)])));
	}
	function start() {
		if (!select) return;
		create.mutate(select, {
			onSuccess: (r) => {
				setConfirm(false);
				onStarted(r.batch_id);
			},
		});
	}

	if (!hasKey)
		return (
			<Card data-testid="plan-block-launch">
				<CardContent className="p-4">
					<EmptyState
						title="未配置 LLM key"
						desc="评测要用真模型对话（机器顾客 ＋ 被测 agent ＋ 判官），服务器 .env 配置 LLM_API_KEY 后再发起。"
					/>
				</CardContent>
			</Card>
		);

	const setLabel = pickedSet
		? `按测试集跑 ${setInfo?.n ?? "?"} 题`
		: `筛出 ${shown.length} · 已选 ${picked.length}`;

	return (
		<>
			{/* 置顶操作条：筛选 ＋ 选题 ＋ 次数 ＋ 估价 ＋ 开始（本页唯一实色按钮） */}
			<Card className="sticky top-0 z-10" data-testid="plan-block-launch">
				<CardContent className="space-y-3 px-4 py-3">
					<ScenarioFilters
						scenarios={scenarios}
						setNames={(sets.data?.sets ?? []).map((x) => x.name)}
						value={filter}
						onChange={setFilter}
					/>
					<div className="flex flex-wrap items-center gap-3 text-xs">
						<span className="inline-flex items-center gap-1 text-text-muted">
							题库 <BankTag id={bankId} />
						</span>
						<span className="inline-flex items-center gap-1.5">
							<span className="text-text-muted">测试集</span>
							<StyledSelect
								aria-label="测试集"
								value={pickedSet}
								onChange={(e) => setPickedSet(e.target.value)}
								disabled={sets.isError}
								title={sets.isError ? `测试集没取到：${planErrorMessage(sets.error)}` : undefined}
							>
								<option value="">手选场景</option>
								{(sets.data?.sets ?? []).map((x) => (
									<option
										key={x.name}
										value={x.name}
										disabled={!!x.retired}
										title={
											x.retired
												? `已退役（${x.retired.retired_at ?? ""}）：${x.retired.reason ?? ""}；改用 ${x.retired.replaced_by ?? "—"}`
												: x.desc
										}
									>
										{x.name}（{x.n}
										{x.sealed ? " · 盲测" : ""}
										{x.retired ? " · 已退役" : ""}）
									</option>
								))}
							</StyledSelect>
						</span>
						<span className="text-text-muted" data-testid="plan-eval-picked">
							{setLabel}
						</span>
						{pickedSet ? null : (
							<>
								<Button type="button" variant="outline" size="sm" onClick={selectShown}>
									全选筛选结果
								</Button>
								<Button type="button" variant="outline" size="sm" onClick={() => setPicked([])}>
									清空
								</Button>
							</>
						)}
						<span className="inline-flex items-center gap-1.5">
							<span className="text-text-muted">每题次数</span>
							<Input
								type="number"
								min={1}
								max={MAX_TRIALS}
								value={trials}
								placeholder="按规定"
								title="不填按规定次数：多诉求题 5 次、其余 3 次"
								onChange={(e) => setTrials(e.target.value)}
								className="h-8 w-20"
								aria-label="每题次数"
							/>
						</span>
						<span
							className="text-text-muted"
							data-testid="plan-eval-estimate"
							title="美元是折算值：DeepSeek 官网按人民币计价，按配置汇率折成美元（汇率见配置版本页「运行环境」）"
						>
							{est.data
								? `估价 $${est.data.estimate_usd.toFixed(2)}${est.data.balance_usd != null ? ` · 余额 $${est.data.balance_usd.toFixed(2)}` : ""}`
								: null}
						</span>
						<span className="ml-auto" title={blocked ?? undefined}>
							<Button
								type="button"
								disabled={!!blocked || create.isPending}
								onClick={() => setConfirm(true)}
								data-testid="plan-eval-start"
							>
								开始评测
							</Button>
						</span>
					</div>
					{blocked && (pickedSet || picked.length) ? (
						<div className="text-xs text-text-muted" data-testid="plan-eval-blocked">
							不能发起：{blocked}
							{est.data?.suggest_split?.length
								? `；建议拆成 ${est.data.suggest_split.length} 批（不降次数）`
								: ""}
						</div>
					) : null}
				</CardContent>
			</Card>

			<Card data-testid="plan-block-scenarios">
				<CardContent className="space-y-3 p-4">
					<BankLineage
						bankId={bankId}
						derivedFrom={q.data?.derived_from}
						note={q.data?.derive_note}
					/>
					{setInfo?.desc ? (
						<p className="text-xs text-text-muted" data-testid="plan-eval-set-desc">
							{setInfo.name}：{setInfo.desc}
						</p>
					) : null}
					<JudgeLegend />
					{q.isLoading ? (
						<LoadingInline text="加载题库快照里的题…" />
					) : q.isError ? (
						<FailState
							title="题目没加载出来"
							reason={planErrorMessage(q.error)}
							onRetry={() => q.refetch()}
							retrying={q.isFetching}
							testId="plan-eval-scenarios-fail"
						/>
					) : (
						<>
							<div className="space-y-1.5">
								{shown.map((s) => (
									<ScenarioRow
										key={s.id}
										s={s}
										checked={pickedSet ? false : picked.includes(s.id)}
										disabled={!!pickedSet}
										onToggle={() => toggle(s.id)}
									/>
								))}
								{shown.length === 0 ? (
									<EmptyState
										title="当前筛选无场景"
										desc={scenarios.length ? "放宽筛选条件再看。" : "题库快照里没有题。"}
									/>
								) : null}
							</div>
							<div className="text-xs text-text-muted">
								共 {shown.length} 条{setInfo ? `（测试集 ${setInfo.name} 共 ${setInfo.n} 题）` : ""}
							</div>
						</>
					)}
				</CardContent>
			</Card>

			<Dialog open={confirm} onOpenChange={setConfirm}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>发起这批评测？</DialogTitle>
						<DialogDescription>
							{pickedSet ? `测试集 ${pickedSet}` : `${picked.length} 题`}
							{totalTrials != null ? `，共 ${totalTrials} 次试跑` : ""}
							{trialsNum ? `（每题 ${trialsNum} 次）` : "（按规定次数：多诉求 5、其余 3）"}
							；性格随机。估价
							{est.data ? ` $${est.data.estimate_usd.toFixed(2)}` : " —"}
							{est.data?.balance_usd != null ? `，余额 $${est.data.balance_usd.toFixed(2)}` : ""}。
							发起后在后台跑、会花钱、不能撤回（可以停批，没跑的记未判定）。
						</DialogDescription>
					</DialogHeader>
					{create.isError ? (
						<p className="text-xs text-brand-danger" data-testid="plan-eval-create-error">
							{planErrorMessage(create.error)}
						</p>
					) : null}
					<DialogFooter>
						<Button type="button" variant="outline" size="sm" onClick={() => setConfirm(false)}>
							取消
						</Button>
						<Button
							type="button"
							size="sm"
							disabled={create.isPending}
							onClick={start}
							data-testid="plan-eval-confirm"
						>
							{create.isPending ? "发起中…" : "确认发起"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

/** 金标状态（题库快照 gold_status） */
const GOLD_LABEL: Record<string, { text: string; tone: Tone }> = {
	ready: { text: "金标就绪", tone: "success" },
	pending_solvable: { text: "金标待验", tone: "warning" },
	needs_fix: { text: "金标待修", tone: "danger" },
};
function GoldPill({ s }: { s: SimScenario }) {
	if (!s.has_gold) return <Pill tone="muted">无金标</Pill>;
	const g = GOLD_LABEL[s.gold_status ?? ""];
	return <Pill tone={g?.tone ?? "muted"}>{g?.text ?? `金标 ${s.gold_status ?? "—"}`}</Pill>;
}

/** 已知问题的出处标注：原文都是 Loop 时期结论，Plan 上没验证过，不显示成「已解」 */
function KnownIssuePill({ ki }: { ki?: SimKnownIssue | null }) {
	if (!ki) return null;
	const title = `${ki.cat}：${ki.note}${ki.ref ? `（${ki.ref}）` : ""}\nPlan 侧：${ki.plan_note}`;
	return (
		<span title={title} data-testid="plan-eval-known-issue">
			<Pill tone={ki.origin === "design" ? "muted" : "warning"}>
				{ki.origin === "design" ? "负例设计" : "Loop 时期结论"}
			</Pill>
		</span>
	);
}

/** 题库快照号 ＋ 派生来源一行（详情在题库快照页） */
function BankLineage({
	bankId,
	derivedFrom,
	note,
}: {
	bankId: string | null;
	derivedFrom?: string | null;
	note?: string | null;
}) {
	if (!bankId) return null;
	return (
		<div
			className="flex min-w-0 items-center gap-1.5 text-xs text-text-muted"
			data-testid="plan-eval-bank-lineage"
		>
			<span className="shrink-0">题库快照</span>
			<BankTag id={bankId} />
			{derivedFrom ? (
				<>
					<span className="shrink-0">派生自</span>
					<BankTag id={derivedFrom} />
				</>
			) : null}
			{note ? (
				<span className="min-w-0 truncate" title={note}>
					：{note}
				</span>
			) : null}
			<span className="shrink-0">
				· 详情见 <PageJump to="eval-bank" />
			</span>
		</div>
	);
}

function ScenarioRow({
	s,
	checked,
	disabled,
	onToggle,
}: {
	s: SimScenario;
	checked: boolean;
	disabled: boolean;
	onToggle: () => void;
}) {
	const ev = s.eval ?? {};
	return (
		<label
			className="flex cursor-pointer items-start gap-2 rounded-md p-2 text-sm hover:bg-surface-subtle"
			data-testid="plan-eval-scenario"
			data-sealed={s.sealed ? "1" : undefined}
		>
			<input
				type="checkbox"
				checked={checked}
				disabled={disabled}
				onChange={onToggle}
				className="mt-1 accent-brand-accent"
			/>
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="font-medium text-text-primary">{s.id}</span>
					{s.family ? <Pill tone="muted">{s.family}</Pill> : null}
					<Pill tone={STAGE_TONE[s.stage] ?? "muted"}>{s.stage}</Pill>
					{s.category ? <Pill tone="info">{s.category}</Pill> : null}
					{s.multi_demand ? <Pill tone="info">多诉求</Pill> : null}
					{s.required_trials ? <Pill tone="muted">规定 {s.required_trials} 次</Pill> : null}
					<GoldPill s={s} />
					{(s.test_sets ?? []).map((t) => (
						<Pill key={t} tone="muted">
							{t}
						</Pill>
					))}
					<KnownIssuePill ki={s.known_issue} />
					{s.sealed ? null : <span className="font-normal text-text-muted">· {s.desc}</span>}
				</div>
				{s.sealed ? (
					<div className="text-xs text-text-muted" data-testid="plan-eval-sealed">
						盲测题，题面不公开（意图 {s.intent || "—"} · 难度 {s.difficulty || "—"}）
					</div>
				) : (
					<>
						<div className="truncate text-xs text-text-muted">来意：{s.reason_for_call}</div>
						<div className="mt-1 flex flex-wrap gap-1">
							{(ev.expect_skills ?? []).map((sk) => (
								<Pill key={sk} tone="info">
									{sk}
								</Pill>
							))}
							{(ev.nl_assertions ?? []).length ? (
								<Pill tone="muted">{(ev.nl_assertions ?? []).length} 条合规断言</Pill>
							) : null}
						</div>
						<JudgeContract ev={ev} />
					</>
				)}
			</div>
		</label>
	);
}

/** 场景筛选 —— 题族 ／ 测试集 ／ 阶段 ／ 意图 ／ 难度 ／ 品类 ／ 已知问题（批 3 删失效的「真实环境」：接口不返回 live） */
export type ScenFilter = {
	family: string;
	testSet: string;
	stage: string;
	intent: string;
	difficulty: string;
	category: string;
	issue: string; // ""=全部 / "有" / "无"
};
export const EMPTY_SCEN_FILTER: ScenFilter = {
	family: "",
	testSet: "",
	stage: "",
	intent: "",
	difficulty: "",
	category: "",
	issue: "",
};
export function filterScenarios(scenarios: SimScenario[], f: ScenFilter): SimScenario[] {
	return scenarios.filter(
		(s) =>
			(!f.family || (s.family ?? "") === f.family) &&
			(!f.testSet || (s.test_sets ?? []).includes(f.testSet)) &&
			(!f.stage || s.stage === f.stage) &&
			(!f.intent || s.intent === f.intent) &&
			(!f.difficulty || s.difficulty === f.difficulty) &&
			(!f.category || (s.category ?? "") === f.category) &&
			(!f.issue || (f.issue === "有" ? !!s.known_issue : !s.known_issue)),
	);
}

export function ScenarioFilters({
	scenarios,
	setNames,
	value,
	onChange,
}: {
	scenarios: SimScenario[];
	/** 测试集筛选的选项（按测试集接口的顺序） */
	setNames: string[];
	value: ScenFilter;
	onChange: (f: ScenFilter) => void;
}) {
	const set = (patch: Partial<ScenFilter>) => onChange({ ...value, ...patch });
	return (
		<div className="flex flex-wrap items-center gap-3 text-xs">
			<FilterSel
				label="题族"
				value={value.family}
				onChange={(v) => set({ family: v })}
				opts={uniqStr(scenarios.map((s) => s.family ?? ""))}
			/>
			<FilterSel
				label="所属测试集"
				value={value.testSet}
				onChange={(v) => set({ testSet: v })}
				opts={setNames}
			/>
			<FilterSel
				label="阶段"
				value={value.stage}
				onChange={(v) => set({ stage: v })}
				opts={uniqStr(scenarios.map((s) => s.stage))}
			/>
			<FilterSel
				label="意图"
				value={value.intent}
				onChange={(v) => set({ intent: v })}
				opts={uniqStr(scenarios.map((s) => s.intent))}
			/>
			<FilterSel
				label="难度"
				value={value.difficulty}
				onChange={(v) => set({ difficulty: v })}
				opts={uniqStr(scenarios.map((s) => s.difficulty))}
			/>
			<FilterSel
				label="品类"
				value={value.category}
				onChange={(v) => set({ category: v })}
				opts={uniqStr(scenarios.map((s) => s.category ?? ""))}
			/>
			<FilterSel
				label="已知问题"
				value={value.issue}
				onChange={(v) => set({ issue: v })}
				opts={["有", "无"]}
			/>
		</div>
	);
}
